/**
 * Tool Sync Bridge
 *
 * The BIM store keeps its own `activeTool` copy because the BIM context
 * menu (ContextMenu.tsx) and the OptionsBar/StatusBar fallbacks read it.
 * Historically, half of the activation paths wrote only the app store and
 * half only the BIM store, so the two drifted apart (wall tool right-click
 * menu never appeared, OptionsBar showed ghost panels, ...).
 *
 * Resolution model: useAppStore.activeTool is the SINGLE source of truth
 * (the tool controller and all tool hooks subscribe to it). This bridge
 * mirrors every app-store change into the BIM store in canonical UPPERCASE
 * form — without touching the selection. Callers that conceptually own a
 * BIM-side activation must write the app store; the bridge keeps the BIM
 * side coherent. The one-way flow makes echo loops structurally impossible.
 */

import { useAppStore } from './store';
import { useBIMStore } from './useBIMStore';

let started = false;

/** Start the app -> BIM activeTool mirror (idempotent). */
export function startToolSyncBridge(): void {
    if (started) return;
    started = true;

    let lastMirrored = '';
    useAppStore.subscribe((state) => {
        const mirror = String(state.activeTool).toUpperCase();
        if (mirror === lastMirrored) return;
        lastMirrored = mirror;
        const bim = useBIMStore.getState();
        if (bim.activeTool !== (mirror as any)) {
            bim.mirrorActiveTool(mirror as any);
        }
    });

    // Mirror the initial value as well (rehydrated persisted state).
    const initial = String(useAppStore.getState().activeTool).toUpperCase();
    lastMirrored = initial;
    useBIMStore.getState().mirrorActiveTool(initial as any);
}
